// AuditDetailPanel — body of the expanded row in the v5 audit table.
// Renders the kv-grid on top and a JSON dump of the raw event below
// (mockup lines 2789–2803). Lazily fetched: parent passes `eventId`, the
// panel calls auditClient.getEventDetail() once on first open and caches
// the result via the parent.

import { useEffect, useState } from "react";
import { auditClient } from "@/data/clients/auditClient";
import { ApiError } from "@/api";
import type { AuditEvent } from "./types";
import { adaptDetail } from "./detailAdapter";
import "./Audit.css";

export interface AuditDetailPanelProps {
  /** The list-row event; used as the fallback render when the detail call
   *  fails or returns null (legacy backend). */
  fallback: AuditEvent;
}

/** Suppress the JSON-dump pre block when changes is null / "" / [] / {} —
 *  events without a diff used to render literal "null" right above the
 *  details JSON dump. */
function hasMeaningfulChanges(changes: unknown): boolean {
  if (changes === null || changes === undefined) return false;
  if (typeof changes === "string") return changes.trim() !== "" && changes !== "null";
  if (Array.isArray(changes)) return changes.length > 0;
  if (typeof changes === "object") return Object.keys(changes).length > 0;
  return true;
}

export function AuditDetailPanel({ fallback }: AuditDetailPanelProps): JSX.Element {
  const [raw, setRaw] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const d = await auditClient.getEventDetail(fallback.eventId);
        if (cancelled) return;
        setRaw(d as unknown as Record<string, unknown> | null);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.code ?? String(err.status));
        else setError("unknown");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fallback.eventId]);

  const { evt, rawDetails, changesJson } = adaptDetail(raw, fallback);

  return (
    <div className="rb-audit__detail">
      {loading && (
        <div className="rb-audit__detail-loading">Loading event details…</div>
      )}
      {error !== null && (
        <div className="rb-audit__detail-loading">
          Couldn't load detail ({error}); showing list-row data.
        </div>
      )}

      <dl className="rb-audit__detail-grid">
        <dt>event_id</dt>
        <dd>{evt.eventId}</dd>

        <dt>timestamp</dt>
        <dd>{evt.timestamp}</dd>

        <dt>event_type</dt>
        <dd>{evt.eventType}</dd>

        <dt>category</dt>
        <dd>{evt.category}</dd>

        <dt>actor.alias</dt>
        <dd>{evt.actor.alias}</dd>

        <dt>actor.kind</dt>
        <dd>{evt.actor.kind}</dd>

        <dt>actor.role</dt>
        <dd>{evt.actor.role || "—"}</dd>

        {evt.target && (
          <>
            <dt>target.kind</dt>
            <dd>{evt.target.kind}</dd>
            <dt>target.id</dt>
            <dd>{evt.target.id}</dd>
            {evt.target.displayName && (
              <>
                <dt>target.name</dt>
                <dd>{evt.target.displayName}</dd>
              </>
            )}
          </>
        )}

        {evt.locationId && (
          <>
            <dt>location_id</dt>
            <dd>{evt.locationId}</dd>
          </>
        )}

        {evt.requestId && (
          <>
            <dt>request_id</dt>
            <dd>{evt.requestId}</dd>
          </>
        )}

        {evt.sourceIp && (
          <>
            <dt>source_ip</dt>
            <dd>{evt.sourceIp}</dd>
          </>
        )}
      </dl>

      {hasMeaningfulChanges(changesJson) && (
        <pre>
          <code>
            {typeof changesJson === "string"
              ? changesJson
              : JSON.stringify(changesJson, null, 2)}
          </code>
        </pre>
      )}
      {rawDetails !== undefined &&
        rawDetails !== null &&
        Object.keys(rawDetails).length > 0 && (
          <pre>
            <code>{JSON.stringify(rawDetails, null, 2)}</code>
          </pre>
        )}
    </div>
  );
}
